import { Alert, Card } from '@helix-ds/react'
import React, { useId, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { ConnectSubjectsLoad } from '../hooks/useConnectSubjects'
import { subjectLabel } from '../subjectLabel'
import HrcConnectCard from './HrcConnectCard'

/**
 * The health-records section: who this guardian may connect for, and the flow for whoever
 * is selected.
 *
 * The server decides the roster. It returns only subjects this caller is authorized to
 * act for and accepted Health Records Access for, so a tab here can never be refused at
 * enroll — the list and the authorization are the same answer. That is why there is no
 * client-side filtering of who to show.
 *
 * One subject renders with no tab chrome at all, which is both the common case today and
 * the correct shape for an adult who ordered for themselves. Nobody renders nothing: the
 * user has not accepted the consent for anyone, and the home page's outstanding step is
 * what asks them to.
 */
type HrcConnectSectionContentProps = {
  load: ConnectSubjectsLoad
  refresh: () => void
}

export const HrcConnectSectionContent: React.FC<HrcConnectSectionContentProps> = ({ load, refresh }) => {
  const intl = useIntl()
  const tabsId = useId()
  // Unset until the user picks a tab, so the first subject leads and a refresh never moves
  // the tab under them.
  const [activeId, setActiveId] = useState<string | null>(null)

  if (load.phase === 'loading') {
    return (
      <Card padding="lg" role="region" aria-label={intl.formatMessage({ id: 'healthRecords.section.regionLabel' })}>
        <p role="status" className="text-text-body text-sm">
          <FormattedMessage id="healthRecords.section.loading" />
        </p>
      </Card>
    )
  }

  if (load.phase === 'failed') {
    return (
      <Card padding="lg" role="region" aria-label={intl.formatMessage({ id: 'healthRecords.section.regionLabel' })}>
        {/* Never an empty picker: "nobody to connect" is a claim about their family, and we
            do not know that — we only know the request failed. */}
        <Alert color="failure">
          <FormattedMessage id="healthRecords.section.loadFailed" />
        </Alert>
      </Card>
    )
  }

  // Order the same permitted roster for display only; never derive additional eligibility.
  const subjects = [...load.subjects].sort((a, b) => Number(b.role === 'PROBAND') - Number(a.role === 'PROBAND'))
  const active = subjects.find((s) => s.recordSubjectPrincipalId === activeId) ?? subjects[0]
  if (!active) return null

  // No vertical gap: the selected tab and the card are one continuous surface.
  return (
    <div className="flex min-w-0 flex-col">
      {subjects.length > 1 ? (
        <div
          role="tablist"
          aria-label={intl.formatMessage({ id: 'healthRecords.section.tablist' })}
          className="flex flex-wrap gap-2 pt-1"
        >
          {subjects.map((subject) => {
            const selected = subject.recordSubjectPrincipalId === active.recordSubjectPrincipalId
            return (
              <button
                key={subject.recordSubjectPrincipalId}
                id={`${tabsId}-${subject.recordSubjectPrincipalId}`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={`${tabsId}-panel`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setActiveId(subject.recordSubjectPrincipalId)}
                onKeyDown={(event) => {
                  const index = subjects.indexOf(subject)
                  let nextIndex: number
                  switch (event.key) {
                    case 'ArrowRight':
                      nextIndex = (index + 1) % subjects.length
                      break
                    case 'ArrowLeft':
                      nextIndex = (index - 1 + subjects.length) % subjects.length
                      break
                    case 'Home':
                      nextIndex = 0
                      break
                    case 'End':
                      nextIndex = subjects.length - 1
                      break
                    default:
                      return
                  }
                  event.preventDefault()
                  setActiveId(subjects[nextIndex].recordSubjectPrincipalId)
                  event.currentTarget.parentElement
                    ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
                    [nextIndex]?.focus()
                }}
                className={`focus-visible:ring-brand-600/20 relative max-w-full cursor-pointer rounded-t-xl border px-5 py-3 text-sm font-semibold break-words transition focus:outline-none focus-visible:ring-2 ${
                  selected
                    ? // -mb-px overlaps the card's top border, and recolouring only the tab's
                      // bottom border to the card surface erases the seam beneath it.
                      'border-border-base text-brand-600 dark:text-brand-300 border-b-background-primary bg-background-primary z-10 -mb-px'
                    : 'hover:text-brand-700 dark:hover:text-brand-300 text-text-body border-transparent'
                }`}
              >
                {subjectLabel(subject, intl)}
                {subject.connections > 0 ? (
                  <span className="sr-only">
                    {' '}
                    <FormattedMessage id="healthRecords.section.connectedSr" />
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      ) : null}

      {/* Keyed by subject: switching tabs starts a clean flow rather than carrying one
          person's in-flight handoff over to another. */}
      <div
        id={`${tabsId}-panel`}
        role={subjects.length > 1 ? 'tabpanel' : undefined}
        aria-labelledby={subjects.length > 1 ? `${tabsId}-${active.recordSubjectPrincipalId}` : undefined}
      >
        <HrcConnectCard key={active.recordSubjectPrincipalId} subject={active} onConnected={refresh} />
      </div>
    </div>
  )
}
